import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, Loader2, Undo2 } from "lucide-react";

// WHICH PHOTO SHOWS THE TRUCK — OR THE PART (Batch 3b: the same feature, one way). A list renders ONE
// photo per row, so this chooses the one that stands for the record wherever a single photo is shown.
// `scope` is "donor" or "part". A part's default is its first overall shot — never a tag close-up or a
// photo a number was read from.
//
// It sits beside "Wrong photo?" on purpose: that is where somebody is already looking at a photo
// and deciding something about it, and it needs no new screen and no new permission —
// `archive_photos`, his ruling, the same gate as taking a photo off a truck.
//
// `isMain` is the RESOLVED main photo (which may be the derived default), `isOverride` says the
// owner picked it deliberately. Only a deliberate pick can be undone, because clearing a derived
// default would do nothing.
export function MainPhotoAction({ scope = "donor", recordId, fileId, isMain = false, isOverride = false,
                                 onChanged, compact = false, big = false,
                                 testid = "main-photo" }) {
  const { has } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!has("archive_photos") || !recordId || !fileId) return null;
  const noun = scope === "part" ? "part" : "truck";
  const byDefault = scope === "part" ? "its first overall photo (never a tag close-up or a number photo)" : "the first check-in photo";

  const call = async (value, message) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/${scope === "part" ? "parts" : "donors"}/${recordId}/main-photo`, { file_id: value });
      toast.success(message || data.message);
      onChanged?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const shell = compact
    ? "inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 border border-slate-200 text-slate-600 hover:bg-[#E0781A] hover:text-white hover:border-[#E0781A] transition-all"
    : big
      ? "inline-flex items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 h-12 text-base font-bold text-slate-700 active:border-[#E0781A] active:text-[#C76714]"
      : "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 h-9 text-xs font-semibold text-slate-700 hover:border-[#E0781A] hover:text-[#C76714]";
  const picked = compact
    ? "inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#E0781A] border border-[#E0781A] text-white"
    : "inline-flex items-center gap-1.5 rounded-lg border border-[#E0781A] bg-orange-50 px-3 h-9 text-xs font-semibold text-[#C76714]";
  const icon = big ? "w-5 h-5" : "w-3.5 h-3.5";

  // A PART's default photo can be picked as it is: only a person's pick counts to finish it (his rule, 27 Oct).
  const pickable = scope === "part" && isMain && !isOverride;
  if (isMain && !pickable) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span data-testid={`${testid}-current`} className={picked}
          title={isOverride ? `This is the photo chosen to show this ${noun}` : `Shown for this ${noun} by default — ${byDefault}`}>
          <Star className={`${icon} fill-current`} />
          {compact ? "" : isOverride ? "Main photo" : "Main photo (default)"}
        </span>
        {isOverride && !compact && (
          <button type="button" data-testid={`${testid}-clear`} disabled={busy}
            onClick={(e) => { e.stopPropagation(); call(null, "Back to the default photo"); }}
            title={`Go back to the default — ${byDefault}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 underline">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
            Use the default again
          </button>
        )}
      </span>
    );
  }

  return (
    <button type="button" data-testid={`${testid}-set`} disabled={busy}
      onClick={(e) => { e.stopPropagation(); call(fileId); }}
      title={pickable ? "Shown by default now — tap so a person has picked it (needed to finish the part)"
        : `Show this photo for this ${noun} wherever one photo stands for it`} className={shell}>
      {busy ? <Loader2 className={`${icon} animate-spin`} /> : <Star className={icon} />}
      {compact ? "" : "Set as main photo"}
    </button>
  );
}
